import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { backofficeLinks } from "@/components/lib/SidebarLinks";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import useAuth from "@/hooks/useAuth";
import { formatCurrency } from "@/lib/utils";
import CustomerDetailsTab from "./tab/customer-details-tab";
import OrderHistoryTab from "./tab/order-history-tab";
import PointsHistoryTab from "./tab/points-history-tab";
import hasPermission from "@/hooks/hasPermission";
import {
  fetchCustomerDetails,
  fetchCustomerOrderHistory,
} from "@/redux/slice/customer-slice/customer-slice";

function CustomerDetails() {
  const dispatch = useDispatch();
  const { id } = useParams();
  const { user } = useAuth();
  const restaurantId = user?.restaurant_id || 1;

  const hasDetails = hasPermission("back_office.customer.view_details");
  const hasOrders = hasPermission("back_office.customer.order_history");
  const hasPoints = hasPermission("back_office.customer.point_history");

  const defaultTab = hasDetails
    ? "customer-details"
    : hasOrders
    ? "order-history"
    : hasPoints
    ? "points-history"
    : "";

  const {
    customerDetails,
    customerDetailsLoading,
    orderHistoryStats,
  } = useSelector((state) => state.customer.customer);

  const customerId = Number(id);

  useEffect(() => {
    if (!customerId) return;

    dispatch(fetchCustomerDetails(customerId));
    dispatch(
      fetchCustomerOrderHistory({
        restaurantId,
        customerId,
        page: 1,
        per_page: 10,
        search_term: "",
        order_type: "",
        sort_field: "order_id",
        sort_order: "desc",
      }),
    );
  }, [dispatch, customerId, restaurantId]);

  const summaryCards = [
    {
      title: "Total Order",
      value: orderHistoryStats?.total_order ?? 0,
      color: "text-[#5A5DF0]",
    },
    {
      title: "Total Order Amount",
      value: formatCurrency(orderHistoryStats?.total_order_amount || 0, "GBP"),
      color: "text-[#B65BFF]",
    },
    {
      title: "Total used Voucher/gift card",
      value: orderHistoryStats?.total_used_voucher_gift_card ?? 0,
      color: "text-[#32D86A]",
    },
    {
      title: "Total points",
      value: orderHistoryStats?.total_points ?? customerDetails?.points ?? 0,
      color: "text-cyellow",
    },
    {
      title: "Remaining Points",
      value:
        orderHistoryStats?.remaining_points ?? customerDetails?.points_value ?? 0,
      color: "text-[#0A84FF]",
    },
  ];

  return (
    <Layout2
      LeftBar={<LeftBar2 sidebarLinks={backofficeLinks} />}
      pageName={{
        icon: true,
        title: (
          <div className="flex items-center gap-3">
            <span>{`${customerDetails?.full_name || "Customer"} (${customerDetails?.unique_id || "N/A"})`}</span>
            {customerDetails?.is_banned && (
              <span className="bg-red-500/10 text-red-500 border border-red-500/20 text-xs px-2.5 py-0.5 rounded-full font-bold uppercase tracking-wider">
                Banned
              </span>
            )}
          </div>
        ),
      }}
      paths={[
        { name: "Back Office", path: "/backoffice/overview" },
        { name: "Customer", path: "/backoffice/customer" },
        {
          name: "Single Customer Details",
          path: `/backoffice/customer/customer/${id}`,
        },
      ]}
    >
      <div className="container py-5 text-cwhite space-y-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 xl:grid-cols-5">
          {summaryCards.map((item) => (
            <Card
              key={item.title}
              className="border-white/20 bg-cblack4 px-5 py-4"
            >
              <p className="text-sm text-cwhite2">{item.title}</p>
              {customerDetailsLoading && !customerDetails ? (
                <Skeleton className="mt-3 h-8 w-[90px]" />
              ) : (
                <p
                  className={`mt-3 text-2xl leading-none font-medium ${item.color}`}
                >
                  {item.value}
                </p>
              )}
            </Card>
          ))}
        </div>

        <Tabs
          valueKey="customer-single-details-tabs"
          tabName="default"
          defaultValue={defaultTab}
          className="w-full"
        >
          <TabsList className="h-auto w-full justify-start rounded-none border-b border-white/20 bg-transparent p-0">
            {hasDetails && (
              <TabsTrigger
                value="customer-details"
                className="border-r border-white/25 px-4 py-2 text-sm  font-medium text-cwhite2 data-[state=active]:border-b-2 data-[state=active]:border-cyellow data-[state=active]:text-cyellow"
              >
                Customer Details
              </TabsTrigger>
            )}
            {hasOrders && (
              <TabsTrigger
                value="order-history"
                className="border-r border-white/25 px-4 py-2 text-sm  font-medium text-cwhite2 data-[state=active]:border-b-2 data-[state=active]:border-cyellow data-[state=active]:text-cyellow"
              >
                Order History
              </TabsTrigger>
            )}
            {hasPoints && (
              <TabsTrigger
                value="points-history"
                className="px-4 py-2 text-sm  font-medium text-cwhite2 data-[state=active]:border-b-2 data-[state=active]:border-cyellow data-[state=active]:text-cyellow"
              >
                Points History
              </TabsTrigger>
            )}
          </TabsList>

          {hasDetails && (
            <TabsContent value="customer-details" className="mt-6">
              <CustomerDetailsTab
                customerId={customerId}
                customerData={customerDetails}
                loading={customerDetailsLoading}
                restaurantId={restaurantId}
              />
            </TabsContent>
          )}

          {hasOrders && (
            <TabsContent value="order-history" className="mt-6">
              <OrderHistoryTab customerId={customerId} restaurantId={restaurantId} />
            </TabsContent>
          )}

          {hasPoints && (
            <TabsContent value="points-history" className="mt-6">
              <PointsHistoryTab customerId={customerId} restaurantId={restaurantId} />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </Layout2>
  );
}

export default CustomerDetails;
